import React from 'react';
import classnames from 'classnames';
import { injectIntl, IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import { IAttendeeListItemDTO } from '../../../../../api/attendees.dao';
import DefaultButton from '../../../../global/default-button/default-button';
import { HighlightedSearchResult } from '../../shared/highlighted-search-result/highlighted-search-result';
import Text from '../../../../global/text/text';
import { ICustomFieldsDTO } from '../../../../../api/custom-fields.dao';
import { showColumn } from '../../../../../infrastructure/utils/show-column';
import { DateTimeFormatter } from '../../../../../infrastructure/implementations/date-formatter';
import { MultiSelectOption } from '../../../../global/multi-select-list/_multi-select-option';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../api/roles.dao';
import { attendanceMethods } from '../../../../../constants/attendee-values';
import { editText } from '../../../../../constants/common-labels.constants';
import { AttendeeInfo } from '../../../../global/attendee-column/_attendee-info/_attendee-info';
import { OpenFrom } from '../../../../../tracking-service/tracking-values';
import { AttendeeColumnPopoverViewmodel } from '../../../../global/attendee-column/attendee-column-popover/attendee-column-popover.viewmodel';

interface IAttendeesListItemProps {
    attendee: IAttendeeListItemDTO;
    accountId: number;
    eventId: number;
    isSelected: boolean;
    onEdit: () => void;
    isEditable: boolean;
    searchQuery?: string;
    customFields?: ICustomFieldsDTO[];
    handleChangeAttendeeSelection: () => void;
    isAttendeeSelected: boolean;
    intl: IntlShape;
    attendeeDetailsPopoverVM: AttendeeColumnPopoverViewmodel;
}

const dateTimeFormatter = IoC.Container.get<DateTimeFormatter>(DateTimeFormatter);
const attendeeList = 'attendee-list';

const AttendeesListItem = injectIntl((props: IAttendeesListItemProps) => {
    const {
        attendee,
        isSelected,
        onEdit,
        searchQuery,
        isEditable,
        customFields,
        handleChangeAttendeeSelection,
        isAttendeeSelected,
        intl,
        attendeeDetailsPopoverVM,
    } = props;

    const {
        attendeeId,
        organizationName,
        organizationType,
        salutation,
        assistantEmail,
        title,
        email,
        accessCode,
        phoneNumber,
        customFieldValues,
        registrationDate,
        registrationStatus,
        attendanceMethod,
        eventSubscriptionDate,
    } = attendee;

    const cellEffectClasses = classnames({
        'active': isSelected,
        'hoverable': !isSelected
    });

    const formatDateTime = (date: string) => {
        if (!date) return { date: '', time: '' };
        const [formattedDate, formattedTime] = dateTimeFormatter.dateToLocalString(new Date(date)).split(',');
        return {
            date: formattedDate.trim(),
            time: formattedTime.trim()
        };
    };

    const { date: formattedRegistrationDate, time: formattedRegistrationTime } = formatDateTime(registrationDate);
    const { date: formattedSubscribeDate, time: formattedSubscribeTime } = formatDateTime(eventSubscriptionDate);


    return (
        <tr className='attendees-list-item' onDoubleClick={() => isEditable && onEdit()}>
            <HighlightedSearchResult
                searchQuery={searchQuery}
                cellEffectClasses={cellEffectClasses}
                className='attendee-name overflow-ellipsis'
                wrapperTag='td'
            >
                <AttendeeInfo
                    withAttendeePopover={true}
                    attendee={attendee}
                    attendeeDetailsPopoverVM={attendeeDetailsPopoverVM}
                    additionalAttendeeId={attendeeList}
                    openFrom={OpenFrom.Attendees}
                />
            </HighlightedSearchResult>
            {showColumn('email') &&
                <HighlightedSearchResult
                    value={email}
                    searchQuery={searchQuery}
                    cellEffectClasses={cellEffectClasses}
                />}
            {showColumn('organization') &&
                <HighlightedSearchResult
                    value={organizationName}
                    searchQuery={searchQuery}
                    cellEffectClasses={cellEffectClasses}
                    escapeHtml={true}
                />}
            {showColumn('organizationType') &&
                <HighlightedSearchResult
                    value={organizationType}
                    searchQuery={searchQuery}
                    cellEffectClasses={cellEffectClasses}
                />}
            {showColumn('attendanceMethod') &&
                <Text
                    tag='td'
                    className={`${cellEffectClasses} column-with-filters`}
                    message={attendanceMethod ? (attendanceMethods(intl).find(attendanceMethodItem => (attendanceMethodItem.value === attendanceMethod)))?.label : ''}
                    withEllipsis
            />}
            {showColumn('title') &&
                <HighlightedSearchResult
                    value={title}
                    searchQuery={searchQuery}
                    cellEffectClasses={cellEffectClasses}
                    escapeHtml={true}
                />}
            {showColumn('phoneNumber') &&
                <HighlightedSearchResult
                    value={phoneNumber}
                    searchQuery={searchQuery}
                    cellEffectClasses={cellEffectClasses}
                    escapeHtml={true}
                />}
            {showColumn('salutation') &&
                < Text tag='td' className={cellEffectClasses} message={salutation} withEllipsis escapeHtml={true} />}
            {showColumn('assistantEmail') &&
                < Text tag='td' className={cellEffectClasses} message={assistantEmail} withEllipsis />}
            {showColumn('attendeeId') &&
                <Text tag='td' className={cellEffectClasses} message={`${attendeeId}`} withEllipsis />}
            {showColumn('accessCode') &&
                <Text tag='td' className={cellEffectClasses} message={accessCode} withEllipsis />}
            {showColumn('registrationDate') &&
                <td className={cellEffectClasses}>
                    <p>{formattedRegistrationDate}</p>
                    <p>{formattedRegistrationTime}</p>
                </td>}
            {showColumn('registrationStatus') &&
                <Text
                    tag='td'
                    className={`${cellEffectClasses} column-with-filters`}
                    message={registrationStatus}
                    id={`oe.registrationStatus.${registrationStatus}`}
                    withEllipsis
                />}
            {showColumn('eventSubscriptionDate') &&
                <td className={cellEffectClasses}>
                    <p>{formattedSubscribeDate}</p>
                    <p>{formattedSubscribeTime}</p>
                </td>
            }
            {showColumn('bulkAttendeeSelection') && hasPermission(Permission.UPDATE_ATTENDEE) &&
                <td className={cellEffectClasses}>
                    <MultiSelectOption
                        onClick={() => handleChangeAttendeeSelection()}
                        isSelected={isAttendeeSelected}
                    />
                </td>
            }
            {customFields?.map(field => {
                return (
                    showColumn(`${field.customFieldId}`) &&
                    <Text
                        tag='td'
                        className={cellEffectClasses}
                        message={customFieldValues[field.customFieldId] ?? ''}
                        withEllipsis
                        key={field.customFieldId}
                        escapeHtml={true}
                    />
                );
            })}
            <td className={cellEffectClasses} />
            <td className={classnames('align-right', cellEffectClasses)}>
                {isEditable &&
                    <DefaultButton
                        className='visible-while-hovered'
                        onClick={() => onEdit()}
                        modifier='slim'
                        text={editText()}
                    />
                }
            </td>
        </tr>
    );
});

export { AttendeesListItem };
